import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as turf from '@turf/turf';
import { DatePicker, Empty, Switch, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { Feature, Polygon } from 'geojson';
import { Layers, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MapRef } from 'react-map-gl/maplibre';
import { Map, Layer, Source } from 'react-map-gl/maplibre';

import { env } from '@/config/env';
import { MAP_DEFAULTS } from '@/config/map';
import { getVietmapVectorStyleUrl } from '@/config/vietmap';
import { LandCoverPercentBarRow } from '@/features/map/components/ui/LandCoverPercentBarRow';
import { LAND_COVER_LEGEND_SWATCH } from '@/features/map/utils/landCoverCategoryColors';
import {
  buildMockLandCoverGeoJson,
  computeLandCoverDistribution,
  MOCK_LAND_COVER_FILL,
  MOCK_LAND_COVER_STROKE,
} from '@/features/map/utils/mockLandCoverFeaturesInAoi';
import type { AoiLandCoverCategoryKey, AoiLandCoverImpact } from '@/types/flood.types';

const ALL_LAND_KEYS: AoiLandCoverCategoryKey[] = [
  'ricePaddy',
  'urbanBuilt',
  'forest',
  'perennialCrop',
  'surfaceWater',
];

function landCoverLabelKey(k: AoiLandCoverCategoryKey): string {
  return `flood.aoiInsight.landCover.${k}`;
}

function parseRangeDayjs(minIso?: string, maxIso?: string): { min: Dayjs; max: Dayjs } | null {
  if (!minIso?.trim() || !maxIso?.trim()) return null;
  const min = dayjs(minIso);
  const max = dayjs(maxIso);
  if (!min.isValid() || !max.isValid()) return null;
  return min.isAfter(max) ? { min: max, max: min } : { min, max };
}

export interface SarAoiLandCoverMapTabProps {
  /** Polygon AOI (job SAR); thiếu → empty state */
  aoi: Polygon | undefined;
  /** Thống kê ha theo lớp — điều chỉnh tỷ lệ mock */
  landImpacts?: AoiLandCoverImpact[];
  /** Giới hạn DatePicker (ISO), ví dụ từ tham số job */
  dateRangeMinIso?: string;
  dateRangeMaxIso?: string;
}

export function SarAoiLandCoverMapTab({
  aoi,
  landImpacts,
  dateRangeMinIso,
  dateRangeMaxIso,
}: SarAoiLandCoverMapTabProps) {
  const { t } = useTranslation();
  const mapRef = useRef<MapRef>(null);

  const range = useMemo(
    () => parseRangeDayjs(dateRangeMinIso, dateRangeMaxIso),
    [dateRangeMinIso, dateRangeMaxIso],
  );

  const [selectedDate, setSelectedDate] = useState<Dayjs>(() => range?.max ?? dayjs());

  useEffect(() => {
    if (!range) return;
    queueMicrotask(() => {
      setSelectedDate((prev) => {
        if (prev.isBefore(range.min, 'day')) return range.min;
        if (prev.isAfter(range.max, 'day')) return range.max;
        return prev;
      });
    });
  }, [range]);

  const [mapLoadError, setMapLoadError] = useState<string | null>(null);

  const [layerOn, setLayerOn] = useState<Record<AoiLandCoverCategoryKey, boolean>>(() => ({
    ricePaddy: true,
    urbanBuilt: true,
    forest: true,
    perennialCrop: true,
    surfaceWater: true,
  }));

  const aoiFeature = useMemo((): Feature<Polygon> | null => {
    if (!aoi) return null;
    return { type: 'Feature', properties: {}, geometry: aoi };
  }, [aoi]);

  /** GeoJSON lớp phủ mock — sinh từ polygon AOI + tỷ lệ impacts */
  const mockLandCoverGeoJson = useMemo(() => {
    if (!aoi || !landImpacts?.length) return null;
    return buildMockLandCoverGeoJson(aoi, landImpacts, selectedDate.format('YYYY-MM-DD'));
  }, [aoi, landImpacts, selectedDate]);

  const fitAoi = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || !aoi) return;
    const b = turf.bbox({ type: 'Feature', properties: {}, geometry: aoi });
    map.fitBounds(
      [
        [b[0], b[1]],
        [b[2], b[3]],
      ],
      { padding: 52, duration: 0, maxZoom: 17 },
    );
  }, [aoi]);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      const onMapError = (e: { error?: { message?: string } }) => {
        const msg = e?.error?.message?.trim();
        if (msg) setMapLoadError((prev) => prev ?? msg);
      };
      map.on('error', onMapError);
    }
    fitAoi();
  }, [fitAoi]);

  useEffect(() => {
    queueMicrotask(() => {
      setMapLoadError(null);
    });
  }, [aoi]);

  /** Chỉ fit camera khi AOI / ngày đổi — không gắn mockCollection (tránh lặp với mỗi lần tạo GeoJSON). */
  useEffect(() => {
    fitAoi();
  }, [fitAoi]);

  const distributionRows = useMemo(
    () => computeLandCoverDistribution(landImpacts ?? []),
    [landImpacts],
  );

  const totalHaForPct = useMemo(
    () => distributionRows.reduce((a, r) => a + r.areaHa, 0),
    [distributionRows],
  );

  const statsLine = useMemo(() => {
    const rows = landImpacts ?? [];
    if (!rows.length) return null;
    const enabledSum = rows.filter((r) => layerOn[r.categoryKey]).reduce((a, r) => a + r.areaHa, 0);
    const total = rows.reduce((a, r) => a + r.areaHa, 0);
    return t('flood.aoiInsight.landCoverStatsLine', {
      enabledHa: enabledSum.toFixed(1),
      totalHa: total.toFixed(1),
    });
  }, [landImpacts, layerOn, t]);

  /** Lọc GeoJSON chỉ hiển thị các lớp đang bật */
  const visibleMockGeoJson = useMemo(() => {
    if (!mockLandCoverGeoJson) return null;
    const filtered = mockLandCoverGeoJson.features.filter(
      (f) => layerOn[f.properties?.categoryKey as AoiLandCoverCategoryKey],
    );
    return { ...mockLandCoverGeoJson, features: filtered };
  }, [mockLandCoverGeoJson, layerOn]);

  if (!aoi || !aoiFeature) {
    return (
      <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] px-4 py-10">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-[12px] text-slate-500">
              {t('flood.aoiInsight.landCoverNoAoi')}
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div className="lc-tab-root">
      {/* ── Header: ngày xem + thống kê ── */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="space-y-1">
          <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">
            {t('flood.aoiInsight.landCoverViewDate')}
          </span>
          <DatePicker
            value={selectedDate}
            onChange={(d) => d && setSelectedDate(d)}
            allowClear={false}
            className="min-w-[200px]"
            size="middle"
            defaultPickerValue={range?.min ?? selectedDate}
            disabledDate={(d) => {
              if (!d || !range) return false;
              return d.isBefore(range.min, 'day') || d.isAfter(range.max, 'day');
            }}
          />
        </div>
        {statsLine ? (
          <Typography.Text type="secondary" className="text-[11px]">
            {statsLine}
          </Typography.Text>
        ) : null}
      </div>

      {/* ── Layout chính: Bản đồ (trái) + Lớp phủ & Tỷ lệ (phải) ── */}
      <div className="lc-split-layout">
        {/* ── Cột trái: Bản đồ ── */}
        <div className="lc-split-map">
          <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-2 shadow-sm">
            {mapLoadError && env.VIETMAP_TILEMAP_KEY ? (
              <div className="mb-2 rounded-lg bg-red-500/10 px-3 py-2 text-[11px] text-red-700 dark:text-red-400">
                {mapLoadError}
              </div>
            ) : null}
            <div
              className="overflow-hidden rounded-lg border border-[var(--glass-border)]"
              style={{ height: 'min(42vh, 380px)' }}
            >
              {env.VIETMAP_TILEMAP_KEY ? (
                <Map
                  ref={mapRef}
                  mapStyle={getVietmapVectorStyleUrl('dm')}
                  initialViewState={{
                    longitude: MAP_DEFAULTS.CENTER.lng,
                    latitude: MAP_DEFAULTS.CENTER.lat,
                    zoom: 12,
                  }}
                  style={{ width: '100%', height: '100%' }}
                  minZoom={MAP_DEFAULTS.MIN_ZOOM}
                  maxZoom={MAP_DEFAULTS.MAX_ZOOM}
                  onLoad={handleMapLoad}
                >
                  {/* Lớp phủ mock GeoJSON */}
                  {visibleMockGeoJson && visibleMockGeoJson.features.length > 0 ? (
                    <Source id="lc-mock-cover" type="geojson" data={visibleMockGeoJson}>
                      <Layer
                        id="lc-mock-fill"
                        type="fill"
                        paint={{
                          'fill-color': [
                            'match',
                            ['get', 'categoryKey'],
                            'ricePaddy',
                            MOCK_LAND_COVER_FILL.ricePaddy,
                            'urbanBuilt',
                            MOCK_LAND_COVER_FILL.urbanBuilt,
                            'forest',
                            MOCK_LAND_COVER_FILL.forest,
                            'perennialCrop',
                            MOCK_LAND_COVER_FILL.perennialCrop,
                            'surfaceWater',
                            MOCK_LAND_COVER_FILL.surfaceWater,
                            'rgba(128,128,128,0.4)',
                          ],
                        }}
                      />
                      <Layer
                        id="lc-mock-stroke"
                        type="line"
                        paint={{
                          'line-color': [
                            'match',
                            ['get', 'categoryKey'],
                            'ricePaddy',
                            MOCK_LAND_COVER_STROKE.ricePaddy,
                            'urbanBuilt',
                            MOCK_LAND_COVER_STROKE.urbanBuilt,
                            'forest',
                            MOCK_LAND_COVER_STROKE.forest,
                            'perennialCrop',
                            MOCK_LAND_COVER_STROKE.perennialCrop,
                            'surfaceWater',
                            MOCK_LAND_COVER_STROKE.surfaceWater,
                            'rgba(128,128,128,0.8)',
                          ],
                          'line-width': 1,
                          'line-opacity': 0.7,
                        }}
                      />
                    </Source>
                  ) : null}

                  {/* Viền AOI */}
                  <Source id="sar-aoi-outline" type="geojson" data={aoiFeature}>
                    <Layer
                      id="sar-aoi-fill"
                      type="fill"
                      paint={{
                        'fill-color': 'rgba(56, 189, 248, 0.06)',
                        'fill-outline-color': 'rgba(56, 189, 248, 0.45)',
                      }}
                    />
                    <Layer
                      id="sar-aoi-line"
                      type="line"
                      paint={{
                        'line-color': '#38bdf8',
                        'line-width': 2.5,
                        'line-opacity': 0.95,
                        'line-dasharray': [3, 2],
                      }}
                    />
                  </Source>
                </Map>
              ) : (
                <div className="flex h-full items-center justify-center bg-[var(--map-chrome-deep)] px-4 dark:bg-slate-900/40">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span className="text-[12px] text-[var(--mdm-muted)]">
                        {t('flood.aoiInsight.landCoverMapMissingKeyShort')}
                      </span>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Cột phải: Lớp phủ + Tỷ lệ ── */}
        <div className="lc-split-sidebar">
          {/* Legend + Switch */}
          <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--mdm-header-tint)] px-2.5 py-2 dark:bg-slate-950/20">
            <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
              <Layers size={12} className="text-sky-600 dark:text-sky-400" />
              {t('flood.aoiInsight.landCoverLegendTitle')}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {ALL_LAND_KEYS.map((key) => (
                <label
                  key={key}
                  className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[10px] text-slate-700 transition-colors hover:bg-slate-100/60 dark:text-slate-200 dark:hover:bg-slate-800/40"
                >
                  <Switch
                    size="small"
                    checked={layerOn[key]}
                    onChange={(v) => setLayerOn((prev) => ({ ...prev, [key]: v }))}
                  />
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm border border-white/20 shadow-sm"
                    style={{ backgroundColor: LAND_COVER_LEGEND_SWATCH[key] }}
                  />
                  <span className="font-medium">{t(landCoverLabelKey(key))}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Bảng tỷ lệ diện tích */}
          <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] px-2.5 py-2 shadow-sm">
            <div className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
              <TrendingUp size={12} className="text-emerald-600 dark:text-emerald-400" />
              {t('flood.aoiInsight.landCoverTableTitle')}
            </div>
            <div className="space-y-2">
              {distributionRows.map((row) => (
                <LandCoverPercentBarRow
                  key={row.categoryKey}
                  categoryKey={row.categoryKey}
                  label={t(landCoverLabelKey(row.categoryKey))}
                  areaHa={row.areaHa}
                  pctOfTotal={row.pctOfTotal}
                  compact
                />
              ))}
              <div className="border-t border-[var(--glass-border)] pt-2">
                <div className="flex flex-col gap-y-1">
                  <div className="flex items-start justify-between gap-2 text-[11px]">
                    <span className="font-semibold text-slate-800 dark:text-slate-100">
                      {t('flood.aoiInsight.landCoverColTotal')}
                    </span>
                    <span className="shrink-0 tabular-nums font-semibold text-slate-600 dark:text-slate-300">
                      {totalHaForPct.toFixed(1)} ha
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="relative h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200/90 shadow-inner ring-1 ring-inset ring-slate-900/5 dark:bg-slate-700/90 dark:ring-white/10"
                      aria-hidden
                    >
                      {totalHaForPct > 0 ? (
                        <div
                          className="h-2 w-full rounded-full bg-gradient-to-r from-sky-500/90 to-slate-400/90"
                          style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
                        />
                      ) : null}
                    </div>
                    <span className="w-11 shrink-0 text-right text-[10px] font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                      {totalHaForPct > 0 ? '100%' : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Disclaimer ── */}
      <p className="mt-3 text-[10px] leading-snug text-slate-500 dark:text-slate-400">
        {t('flood.aoiInsight.landCoverDisclaimer')}
      </p>
    </div>
  );
}
